import {
  EGroupType,
  IColumnFilter,
  IColumnGroup,
  IColumnSource,
  IColumnValueMapping,
  IControlBlockIndex,
  IGroupExpression,
  IReportConfiguration,
  IReportConfigurationVisualizationQuery,
  MultiHierarchyGroupingLevel
} from '@opengov/reporting-types';
import { IStringConstantExpression } from '@opengov/reporting-types/src/accountSummary/accountSummaryColumns';
import { Draft, produce } from 'immer';
import { cloneDeep, isPlainObject } from 'lodash';

import { IReportConfigurationState } from './records/reportConfigurationRecords';

type ICallback<S, P> = (state: Draft<S>, payload: P) => void;

type IRCCallback<P> = ICallback<IReportConfigurationState, P>;
export type RCVizQuery = IReportConfigurationVisualizationQuery;

export const isSameColumnFilter =
  (a: IColumnFilter) =>
  (b: IColumnFilter): boolean =>
    b.columnName === a.columnName && b.operator === a.operator && b.value === a.value;

export function getAllColumnAlias(draft: Draft<IReportConfiguration>, overrideRowIndex?: number): string[] {
  const columnGroups = isNaN(overrideRowIndex)
    ? draft.accountSummary.columnGroups
    : draft.accountSummary.rowGroups[overrideRowIndex]['columnGroups'];
  const allColumnAlias = columnGroups.map((group) => {
    if (group.type === EGroupType.Expression) {
      return (group as IGroupExpression).alias;
    } else if (group.type === EGroupType.Lens) {
      const projection = (group as IColumnGroup).grouping[0];
      return (projection as IStringConstantExpression).constant;
    }
  });
  return allColumnAlias;
}

// TODO: add tests
export function removeAndUpdateBlocks(blocks: IControlBlockIndex[], index: number): IControlBlockIndex[] {
  const indexOfBlockToDelete = blocks.findIndex((i) => i.controlBlockIndex === index);

  if (indexOfBlockToDelete > -1) {
    blocks.splice(indexOfBlockToDelete, 1);
    return blocks.map((b) => {
      if (b.controlBlockIndex > index) {
        b.controlBlockIndex = b.controlBlockIndex - 1;
      }
      return b;
    });
  } else if (blocks.length > 0) {
    return blocks.map((b) => {
      if (b.controlBlockIndex > index) {
        b.controlBlockIndex = b.controlBlockIndex - 1;
      }
      return b;
    });
  }
  return blocks;
}

// Return account summary column groups after updated the dependencies for column groups
export function updateCalcColumnGroupDependencies(
  accountSummaryColumnGroups: Draft<IColumnSource>[],
  originalColumnGroupName: string,
  updatedColumnGroupName: string
): Draft<IColumnSource>[] {
  const updatedColumnGroups = cloneDeep(accountSummaryColumnGroups);
  const columnGroups = JSON.parse(JSON.stringify(updatedColumnGroups));
  //Iterate through all column groups , as only calculated groups contain operands, and update their old dependencies
  columnGroups.map((group, index) => {
    if (group.type === EGroupType.Expression) {
      // We only care about index 0 (alias)
      const operandA = columnGroups[index].operands.a[0];
      const operandB = columnGroups[index].operands.b[0];
      if (operandA === originalColumnGroupName) {
        (updatedColumnGroups[index] as any).operands.a[0] = updatedColumnGroupName;
      }
      if (operandB === originalColumnGroupName) {
        (updatedColumnGroups[index] as any).operands.b[0] = updatedColumnGroupName;
      }
    }
  });

  return updatedColumnGroups;
}

export function withNewReportConfig<P>(callback: (state: Draft<IReportConfiguration>, payload: P) => void) {
  return (draft: Draft<IReportConfigurationState>, payload: P): void => {
    draft.currentReportConfiguration.id = null;
    callback(draft.currentReportConfiguration, payload);
  };
}

type IRCVizCallback<P> = ICallback<IReportConfigurationVisualizationQuery, P>;

export const withVisualizationQuery = <P>(callback: IRCVizCallback<P>): IRCCallback<P> =>
  withNewReportConfig((draft, payload) => {
    callback(draft.visualizationQuery, payload);
  });

export function withImmerReducer<T, P>(callback: (state: Draft<T>, payload: P) => void) {
  return (oldState: T, payload: P): T =>
    produce(oldState, (draft) => {
      callback(draft, payload);
    });
}

/**
 * `__proto__` is the only key that reparents the object being built rather than
 * adding a data key to it, so it is the only one worth dropping. `constructor`
 * and `prototype` are ordinary own properties on a plain object and are left
 * alone, since a mapping key is a column value and either could be legitimate data.
 */
const isSafeMappingKey = (key: string): boolean => key !== '__proto__';

const hasOwnKey = (mapping: IColumnValueMapping, key: string): boolean => Object.hasOwn(mapping, key);

/** Entries that are safe to write and are not the '' / undefined removal sentinel. */
const assignableEntries = (mapping: IColumnValueMapping): [string, IColumnValueMapping[string]][] =>
  Object.entries(mapping).filter(([key, value]) => isSafeMappingKey(key) && value !== '' && value !== undefined);

/** Returned when the update mapping removes the key rather than supplying a value for it. */
const DROP_KEY = Symbol('dropKey');

/** Resolves what a single key from the base mapping should become in the merged result. */
const resolveBaseKey = (
  base: IColumnValueMapping,
  update: IColumnValueMapping,
  key: string
): IColumnValueMapping[string] | typeof DROP_KEY => {
  if (!hasOwnKey(update, key)) {
    // Key not in update, keep original value
    return base[key];
  }
  const updateValue = update[key];
  if (updateValue === '' || updateValue === undefined) {
    // Remove key if update value is empty string or undefined
    return DROP_KEY;
  }
  if (isPlainObject(base[key]) && isPlainObject(updateValue)) {
    // Both are objects, recurse
    return mergeColumnValueMapping(base[key] as IColumnValueMapping, updateValue as IColumnValueMapping);
  }
  // Overwrite with update value
  return updateValue;
};

/**
 * Represents a mapping from string keys to string values or nested mappings.
 */
/**
 * Recursively merges two column value mappings.
 * - Overwrites values from update unless value is '' (removal).
 * - Recurses into nested objects.
 * - Returns a new mapping.
 * - Reads own keys only, so inherited members are never copied in as data.
 */
export function mergeColumnValueMapping(
  base: IColumnValueMapping = {},
  update: IColumnValueMapping = {}
): IColumnValueMapping {
  // If update is empty or undefined, return the original mapping as-is
  if (Object.keys(update).length === 0) {
    return base;
  }
  // If base is undefined or empty, return only non-empty update keys
  if (Object.keys(base).length === 0) {
    const result: IColumnValueMapping = {};
    for (const [key, value] of assignableEntries(update)) {
      result[key] = value;
    }
    return result;
  }

  const newMapping: IColumnValueMapping = {};

  // Merge keys from the base mapping. `key` is filtered by isSafeMappingKey, so `__proto__`
  // never reaches the assignment below.
  for (const key of Object.keys(base)) {
    if (!isSafeMappingKey(key)) {
      continue;
    }
    const mergedValue = resolveBaseKey(base, update, key);
    if (mergedValue !== DROP_KEY) {
      newMapping[key] = mergedValue; // legit:ignore-sast
    }
  }

  // Add new keys from update
  for (const [key, value] of assignableEntries(update)) {
    if (!hasOwnKey(base, key)) {
      newMapping[key] = value;
    }
  }

  return newMapping;
}

/**
 * Extracts IDs of column groups that reference a deleted COA group but were retained
 * in the config rather than removed. A column group referencing a deleted COA group is
 * retained when it is the last or only column group left in the report. The frontend
 * skips these when calling /pivot/columns. The user can still see them in the report
 * and edit/fix them.
 */
function getRetainedButInvalidColumnGroupIds(reportConfiguration: IReportConfiguration): Set<string> {
  const retainedButInvalidIds = (reportConfiguration.metadata?.deletedColumns ?? []).flatMap((ref) =>
    ref.usedAs
      .filter(
        (usage): usage is Extract<typeof usage, { status: 'retained-but-invalid' }> =>
          usage.status === 'retained-but-invalid'
      )
      .flatMap((usage) => usage.retainedColumnGroupIds)
  );
  return new Set(retainedButInvalidIds);
}

/**
 * Returns column groups that can be sent to /pivot/columns (excludes retained-but-invalid ones).
 */
export function getValidColumnGroups(reportConfiguration: IReportConfiguration) {
  const retainedButInvalidIds = getRetainedButInvalidColumnGroupIds(reportConfiguration);
  return reportConfiguration.accountSummary?.columnGroups?.filter((cg) => !retainedButInvalidIds.has(cg.id)) ?? [];
}

/**
 * A saved root level as read from a report configuration. The persistence schema requires only
 * `columnName` on a text-column level, so a level saved from outside the app may omit `groupIds`.
 */
type SavedRootLevel = Omit<MultiHierarchyGroupingLevel, 'groupIds'> & { readonly groupIds?: readonly string[] };

/**
 * The id of the root row the grid renders `rowId` as under the row group's current grouping: the id
 * itself for a merge or an unmerged root group, the owning merge's id for a group a merge absorbed
 * (it renders no row of its own), and undefined once it is not a root at all.
 */
export function getMultiHierarchyRootRowId(
  rowGroup: { grouping?: readonly SavedRootLevel[] },
  rowId: string
): string | undefined {
  const rootLevel = rowGroup.grouping?.[0];
  const merges = rootLevel?.mergedGroups ?? [];
  if (merges.some((merge) => merge.id === rowId)) {
    return rowId;
  }
  if (!rootLevel?.groupIds?.includes(rowId)) {
    return undefined;
  }
  return merges.find((merge) => merge.groupIds.includes(rowId))?.id ?? rowId;
}
